import React, { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext } from "react";
import { findDOMNode } from "react-dom";
import scrollIntoView from "scroll-into-view";
import PropTypes from "prop-types";

const AUTO_SCROLL_TIME = 3000;

const ScrollContext = createContext({
  register: () => {},
  unregister: () => {},
});

const ScrollView = ({ children, autoScroll }) => {
  const [, setIndex] = useState(0);
  const elements = useRef({});
  const timer = useRef(null);

  const scrollIndex = useCallback((newIndex) => {
    // eslint-disable-next-line react/no-find-dom-node
    const node = findDOMNode(elements.current[newIndex]);
    if (node) {
      scrollIntoView(node, {
        time: 500,
        align: {
          left: 0,
        },
        validTarget: function (target, parentsScrolled) {
          return (
            parentsScrolled < 2 &&
            target !== window &&
            !target.matches(".dontScroll")
          );
        },
        isScrollable: () => true,
      });
      setIndex(newIndex);
    }
  }, []);

  useEffect(() => {
    if (autoScroll) {
      timer.current = setInterval(() => {
        const numElements = Object.keys(elements.current).length;
        setIndex((prevIndex) => {
          const nextIndex = prevIndex === numElements - 1 ? 0 : prevIndex + 1;
          scrollIndex(nextIndex);
          return nextIndex;
        });
      }, AUTO_SCROLL_TIME);
    }
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [autoScroll, scrollIndex]);

  const register = useCallback((index, ref) => {
    elements.current[index] = ref;
  }, []);

  const unregister = useCallback((index) => {
    delete elements.current[index];
  }, []);

  const contextValue = useMemo(() => ({
    register,
    unregister,
  }), [register, unregister]);

  return (
    <ScrollContext.Provider value={contextValue}>
      {React.Children.only(children)}
    </ScrollContext.Provider>
  );
};

ScrollView.propTypes = {
  children: PropTypes.node,
  autoScroll: PropTypes.bool,
};

const ScrollElement = ({ index, children, style }) => {
  const { register, unregister } = useContext(ScrollContext);
  const elementRef = useRef(null);

  useEffect(() => {
    register(index, elementRef.current);
    return () => unregister(index);
  }, [index, register, unregister]);

  return React.cloneElement(React.Children.only(children), {
    ref: elementRef,
    style,
  });
};

ScrollElement.propTypes = {
  index: PropTypes.number.isRequired,
  children: PropTypes.element.isRequired,
  style: PropTypes.object,
};

export { ScrollElement };
export default ScrollView;
